import { HttpHeaders, HttpErrorResponse, HTTP_INTERCEPTORS } from '@angular/common/http';
import * as i1$2 from '@auth0/angular-jwt';
import { JwtModule, JWT_OPTIONS } from '@auth0/angular-jwt';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Component, NgModule } from '@angular/core';
import { BehaviorSubject, ReplaySubject, of, timer } from 'rxjs';
import { map, catchError, distinctUntilChanged, flatMap, tap } from 'rxjs/operators';
import * as auth0 from 'auth0-js';
import * as i1$1 from '@angular/router';
import * as i1 from '@dat/angular-core';
import { DatHttpRequest } from '@dat/angular-core';

var BrowserStorageTypes;
(function (BrowserStorageTypes) {
    BrowserStorageTypes[BrowserStorageTypes["Local"] = 0] = "Local";
    BrowserStorageTypes[BrowserStorageTypes["Session"] = 1] = "Session";
})(BrowserStorageTypes || (BrowserStorageTypes = {}));
class BrowserWindow {
    constructor() {
        this.win = undefined;
        this.win = window;
    }
    static setCacheValue(key, value, storageType = BrowserStorageTypes.Local) {
        if (key.trim().length > 0 && value !== null && value !== undefined) {
            if (storageType === BrowserStorageTypes.Session) {
                window.sessionStorage.setItem(key, JSON.stringify(value));
            }
            else {
                window.localStorage.setItem(key, JSON.stringify(value));
            }
        }
    }
    static getCacheValue(key, storageType = BrowserStorageTypes.Local) {
        let cacheValue;
        if (key.trim().length > 0) {
            if (storageType === BrowserStorageTypes.Session) {
                cacheValue = JSON.parse(window.sessionStorage.getItem(key));
            }
            else {
                cacheValue = JSON.parse(window.localStorage.getItem(key));
            }
        }
        return cacheValue;
    }
    static purgeCacheValue(key, storageType = BrowserStorageTypes.Local) {
        if (key.trim().length > 0) {
            if (storageType === BrowserStorageTypes.Session) {
                window.sessionStorage.removeItem(key);
            }
            else {
                window.localStorage.removeItem(key);
            }
        }
    }
    get opener() {
        return this.win.opener;
    }
    get onbeforeunload() {
        return this.win.onbeforeunload;
    }
    get browserWindow() {
        return this.win;
    }
    // This class must be assigned a window before usage
    set browserWindow(win) {
        this.win = win;
    }
    get location() {
        return this.win ? this.win.location : undefined;
    }
    get closed() {
        return this.win ? this.win.closed : true;
    }
    // open and return a new window
    // replace is now deprecated, keeping it in method signature for backwards compatibility
    open(url, target, features, replace) {
        const newWindow = new BrowserWindow();
        newWindow.browserWindow = window.open(url, target, features);
        return newWindow;
    }
    close() {
        this.win.close();
    }
    focus() {
        this.win.focus();
    }
    postMessage(message, targetOrigin, transfer) {
        if (this.win && this.win.postMessage) {
            this.win.postMessage(message, targetOrigin, transfer);
        }
    }
    addEventListener(type, listener, useCapture) {
        this.win.addEventListener(type, listener, useCapture);
    }
}

var PageMode;
(function (PageMode) {
    PageMode["LEGACY"] = "legacy";
    PageMode["EMAIL_ONLY"] = "email_only";
    PageMode["STANDARD"] = "standard";
})(PageMode || (PageMode = {}));

class AuthConfiguration {
    constructor(overrides) {
        this._authConfig = Object.assign({
            pageMode: PageMode.STANDARD,
            callbackPath: '/callback',
            loginIfApiCallNotAuthorized: true,
            extendedApiTargetedDomains: []
        }, overrides);
        const requiredOAuth2Values = ['audience', 'clientId', 'clientDomain'];
        if (!this._authConfig.appName) {
            throw new Error('AuthConfiguration: an app name must be provided.');
        }
        else if (!this._authConfig.apiBaseUrl) {
            throw new Error('AuthConfiguration: an API base URL must be provided.');
        }
        else if (!this._authConfig.oAuth2Config ||
            !requiredOAuth2Values.every((key) => this._authConfig.oAuth2Config.hasOwnProperty(key) && this._authConfig.oAuth2Config[key])) {
            throw new Error(`AuthConfiguration: values for oAuth2: ${requiredOAuth2Values.join(';')} must be provided.`);
        }
    }
    get loginIfApiCallNotAuthorized() {
        return this._authConfig.loginIfApiCallNotAuthorized;
    }
    set loginIfApiCallNotAuthorized(value) {
        this._authConfig.loginIfApiCallNotAuthorized = value;
    }
    get appName() {
        return this._authConfig.appName;
    }
    get apiBaseUrl() {
        return this._authConfig.apiBaseUrl;
    }
    get pageMode() {
        return this._authConfig.pageMode;
    }
    get screenHint() {
        return this._authConfig.screenHint;
    }
    get oAuth2Config() {
        return this._authConfig.oAuth2Config;
    }
    get callbackPath() {
        return this._authConfig.callbackPath;
    }
    get extendedApiTargetedDomains() {
        return this._authConfig.extendedApiTargetedDomains;
    }
}
AuthConfiguration.apiTargetedDomains = [
    'localhost:3000',
    'localhost:3200',
    'api.dat-apps.com',
    'api.test.dat.com',
    'api-legacy.test.dat.com',
    'api.staging.dat.com',
    'api-legacy.staging.dat.com',
    'api.dat.com',
    'api-legacy.dat.com',
    'freight.api.nprod.dat.com',
    'freight.api.dat.com',
    'freight.api.prod.dat.com',
    'identity.api.nprod.dat.com',
    'identity.api.dat.com',
    'identity.api.prod.dat.com',
    'analytics.api.nprod.dat.com',
    'analytics.api.dat.com',
    'analytics.api.prod.dat.com',
    'visibility.api.nprod.dat.com',
    'visibility.api.dat.com',
    'visibility.api.prod.dat.com',
    'network.api.nprod.dat.com',
    'network.api.dat.com',
    'network.api.prod.dat.com',
    'enablement.api.nprod.dat.com',
    'enablement.api.dat.com',
    'enablement.api.prod.dat.com',
    'billing.api.nprod.dat.com',
    'billing.api.dat.com',
    'billing.api.prod.dat.com',
    'provisioning.api.nprod.dat.com',
    'provisioning.api.dat.com',
    'provisioning.api.prod.dat.com'
];
const AUTH_CONFIG = new InjectionToken('AUTH_CONFIG');

var ViewTypes;
(function (ViewTypes) {
    ViewTypes["Login"] = "login";
    ViewTypes["Signup"] = "signup";
})(ViewTypes || (ViewTypes = {}));

const dateTimeProvider = {
    now() {
        return Date.now();
    }
};

class UserAuthorizations {
    constructor(authorizations) {
        this.authorizations = authorizations;
        this.roles = [];
        this.permissions = [];
        if (authorizations) {
            this.roles = authorizations.roles;
            this.permissions = authorizations.permissions;
        }
    }
}
class UserPermissions {
}
class UserRoles {
}

class AuthApiService {
    constructor(httpClient, config) {
        this.httpClient = httpClient;
        this.config = config;
        this.options = {
            headers: new HttpHeaders().set('content-type', 'application/json')
        };
        this.apiBaseUrl = this.config.apiBaseUrl;
    }
    getAuthorizations() {
        const authorizationsResponse = {
            roles: [],
            permissions: []
        };
        const request = new DatHttpRequest()
            .withServiceRootUri(this.apiBaseUrl + '/auth/token/authorizations/v1')
            .withHttpOptions(this.options)
            .withAppOptions(DatHttpRequest.concealedRequest);
        return this.httpClient.get(request).pipe(map((res) => {
            authorizationsResponse.roles = res.authorizations.roles;
            authorizationsResponse.permissions = res.authorizations.permissions;
            return new UserAuthorizations(authorizationsResponse);
        }), catchError(this.handleError.bind(this)));
    }
    handleError(error) {
        console.debug('Get authorizations map error: ' + error);
    }
}
AuthApiService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthApiService, deps: [{ token: i1.DatHttpService }, { token: AUTH_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
AuthApiService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthApiService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthApiService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.DatHttpService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG]
                }] }]; } });

class AuthService {
    constructor(router, config, apiService) {
        this.router = router;
        this.config = config;
        this.apiService = apiService;
        this.PROFILE = 'profile';
        this.TOKEN_EXPIRES_BUFFER_SECONDS = 120;
        this.SCOPE = 'openid profile email';
        this.RESPONSE_TYPE = 'token id_token';
        this.HLP_ERRORS = ['email_not_verified', 'email_not_present', 'error_customer_system_user_provision'];
        this.APPLICATION_STATE_KEY = 'ask';
        this.LOGIN_STATE_KEY = 'lsk';
        this.sessionCache = {
            accessToken: '',
            idToken: '',
            expiresAt: 0
        };
        this.LOGIN_COMMANDS = [''];
        this.storeApplicationState();
        this.auth0 = new auth0.WebAuth({
            domain: this.config.oAuth2Config.clientDomain,
            clientID: this.config.oAuth2Config.clientId,
            redirectUri: window.location.origin + this.config.callbackPath,
            responseType: this.RESPONSE_TYPE,
            scope: this.SCOPE,
            audience: this.config.oAuth2Config.audience,
            state: BrowserWindow.getCacheValue(this.APPLICATION_STATE_KEY, BrowserStorageTypes.Session)
        });
        this.createLoggedInResultObservable();
        this.createOAuthProfileResultObservable();
        this.createUserAuthorizationsResultObservable();
        if (this.validateAccessToken()) {
            this.loadUserAuthState();
        }
    }
    createLoggedInResultObservable() {
        this.loggedInSource = new BehaviorSubject(false);
        this.loggedIn$ = this.loggedInSource.asObservable().pipe(distinctUntilChanged());
    }
    createOAuthProfileResultObservable() {
        this.oAuthProfileResultSource = new ReplaySubject(1);
        this.oAuthProfileResult$ = this.oAuthProfileResultSource.asObservable();
    }
    createUserAuthorizationsResultObservable() {
        this.userAuthorizationsResultSource = new ReplaySubject(1);
        this.userAuthorizationsResult$ = this.userAuthorizationsResultSource.asObservable();
    }
    loadUserAuthState() {
        // using setTimeout to avoid an angular stream error due to this HTTP call
        // starting from within the service constructor.  setTimeout places this call outside
        // of the constructor's call stack, and effectively makes it fire-and-forget
        setTimeout(() => { this.fetchProfileAndAuthorizationsAndUpdateLoginStatus(); });
    }
    handleAuthentication() {
        const storedState = {
            state: BrowserWindow.getCacheValue(this.APPLICATION_STATE_KEY, BrowserStorageTypes.Session)
        };
        this.auth0.parseHash(storedState, (error, authResult) => this.handleParse(error, authResult));
    }
    async handleParse(error, authResult) {
        if (error) {
            if (error.errorDescription && this.HLP_ERRORS.includes(error.errorDescription)) {
                const storedLoginState = this.retrieveLoginState();
                this.login(error.errorDescription, storedLoginState.emailAddress, storedLoginState.loginRuntimeOverrides);
                return Promise.reject(error.errorDescription);
            }
            else {
                console.debug('Error returned when parsing authResult hash: ' + error.errorDescription);
            }
        }
        else {
            if (authResult && authResult.accessToken && authResult.idToken) {
                this.setSession(authResult);
                await this.fetchProfileAndAuthorizationsAndUpdateLoginStatus(authResult.accessToken);
            }
        }
        return this.router.navigateByUrl(BrowserWindow.getCacheValue(this.APPLICATION_STATE_KEY, BrowserStorageTypes.Session));
    }
    // calling the Auth0 hosted login page (HLP) and use Implicit Grant to get JWT access_token
    login(errorDescription = '', emailAddress = '', runtimeOverrides = {}) {
        this.clearSession();
        this.storeLoginState(emailAddress, runtimeOverrides);
        const cid = this.getQueryParamValue('cid');
        const phoneNumberEncrypted = this.getQueryParamValue('phone_number_encrypted');
        const signupType = this.getQueryParamValue('signup');
        const product = this.getQueryParamValue('product');
        const ios = this.getQueryParamValue('ios');
        const screenHint = runtimeOverrides.screenHint || this.config.screenHint;
        const loginParams = {
            app_name: this.config.appName,
            page_mode: runtimeOverrides.pageMode || this.config.pageMode,
            init_username: emailAddress,
            view: runtimeOverrides.viewInitialization || ViewTypes.Login,
            email_readonly: runtimeOverrides.isEmailReadOnly || false,
            prompt: 'login',
            ...((screenHint) && { screen_hint: screenHint })
        };
        if (cid) {
            loginParams.cid = cid;
        }
        if (phoneNumberEncrypted) {
            loginParams.phone_number_encrypted = phoneNumberEncrypted;
        }
        if (signupType) {
            loginParams.signup = signupType;
        }
        if (errorDescription) {
            loginParams.error_mode = errorDescription;
        }
        if (ios) {
            loginParams.ios = ios;
        }
        if (product) {
            loginParams.product = product;
        }
        const extKeys = ['page_mode', 'error_mode', 'cid', 'ios', 'product'];
        extKeys.forEach(k => {
            if (loginParams[k])
                loginParams[`ext-${k}`] = loginParams[k];
        });
        this.auth0.authorize(loginParams);
    }
    isAuthenticated() {
        const authenticatedValue = this.validateAccessToken();
        this.loggedInSource.next(authenticatedValue);
        return authenticatedValue;
    }
    getTokenSilently() {
        const token = this.getSession().accessToken; // try to retrieve from cache storage
        if (this.isAuthenticated()) {
            return Promise.resolve(token);
        }
        return this.renewToken();
    }
    renewToken() {
        // If a current request is in progress return the same Promise
        if (this.renewPromise) {
            return this.renewPromise;
        }
        // If not use checkSession and return the promise (do SSO)
        this.renewPromise = new Promise((resolve, reject) => this.auth0.checkSession({}, (err, result) => {
            if (err) {
                // No client session so reauthenticate and all pending XHR requests should
                // fail if they originated from angular's HttpClient
                console.debug('checkSession - failed to renew');
                reject(err);
            }
            else {
                console.debug(`Successfully renewed access token.`);
                this.setSession(result);
                this.fetchProfileAndAuthorizationsAndUpdateLoginStatus(result.accessToken);
                // Notify all requests waiting
                resolve(result.accessToken);
            }
            this.renewPromise = null;
        }));
        return this.renewPromise;
    }
    clearSession() {
        // <deprecated>
        // keep this code until (~v2.2.0+) so that values previously in local storage are removed
        BrowserWindow.purgeCacheValue('access_token');
        BrowserWindow.purgeCacheValue('id_token');
        BrowserWindow.purgeCacheValue('expires_at');
        // </deprecated>
        this.sessionCache.accessToken = '';
        this.sessionCache.idToken = '';
        this.sessionCache.expiresAt = 0;
        BrowserWindow.purgeCacheValue(this.PROFILE);
        this.oAuthProfileResultSource.next(null);
    }
    logout() {
        this.clearSession();
        this.unscheduleRenewal();
        this.loggedInSource.next(false);
        this.auth0.logout({
            returnTo: this.getLoginUrl(),
            clientID: this.config.oAuth2Config.clientId,
        });
    }
    validateAccessToken() {
        const expiresAt = this.getSession().expiresAt;
        const nowDate = new Date();
        let expireDate = null;
        try {
            expireDate = new Date(expiresAt);
        }
        catch (err) { }
        return (expireDate === null) ? false : (nowDate < expireDate);
    }
    getLoginCommands() {
        return this.LOGIN_COMMANDS;
    }
    profileExists() {
        const profileObj = BrowserWindow.getCacheValue(this.PROFILE);
        return (profileObj && profileObj.sub) ? true : false;
    }
    scheduleRenewal(path) {
        if (!this.isAuthenticated()) {
            if (path && path.toLowerCase() === this.config.callbackPath) {
                return;
            }
            else {
                this.renewToken()
                    .then((token) => {
                    console.debug('Schedule renewal - refresh subscription, got new access token');
                })
                    .catch(() => {
                });
                return;
            }
        }
        this.unscheduleRenewal();
        const bufferTime = this.TOKEN_EXPIRES_BUFFER_SECONDS * 1000;
        const expiresAt = this.getSession().expiresAt;
        const source = of(expiresAt).pipe(flatMap((expires) => {
            const now = Date.now();
            // Use the delay in a timer to run the renew at the proper time
            return timer(Math.max(1, (expiresAt - bufferTime) - now));
        }));
        this.refreshSubscription = source.subscribe(() => {
            this.renewToken()
                .then((token) => {
                console.debug('Schedule renewal - refresh subscription, got new access token');
            })
                .catch(() => {
            });
        });
    }
    getLoginUrl() {
        return window.location.origin + this.router.createUrlTree(this.getLoginCommands()).toString();
    }
    unscheduleRenewal() {
        if (!this.refreshSubscription) {
            return;
        }
        this.refreshSubscription.unsubscribe();
    }
    fetchProfileAndAuthorizationsAndUpdateLoginStatus(accessToken) {
        if (!accessToken) {
            accessToken = this.getSession().accessToken;
        }
        return new Promise((resolve, reject) => {
            const promiseProfile = this.fetchProfile(accessToken);
            const promiseAuthz = this.fetchAuthorizations();
            Promise.all([promiseProfile, promiseAuthz]).then(values => {
                this.loggedInSource.next(true);
                resolve(values);
            }).catch(reason => {
                this.loggedInSource.next(false);
                reject(reason);
            });
        });
    }
    fetchProfile(accessToken) {
        return new Promise((resolve, reject) => {
            // note: fetch oauth profile only if not already cached
            if (this.profileExists()) {
                const profile = BrowserWindow.getCacheValue(this.PROFILE);
                this.oAuthProfileResultSource.next(profile);
                resolve(profile);
            }
            else {
                this.auth0.client.userInfo(accessToken, (err, profile) => {
                    if (err) {
                        reject(err);
                    }
                    else {
                        BrowserWindow.setCacheValue(this.PROFILE, profile);
                        this.oAuthProfileResultSource.next(profile);
                        resolve(profile);
                    }
                });
            }
        });
    }
    fetchAuthorizations() {
        return new Promise((resolve, reject) => this.apiService.getAuthorizations().subscribe((result) => {
            this.userAuthorizationsResultSource.next(result);
            resolve(result);
        }, (error) => reject(error)));
    }
    setSession(authResult) {
        const tokenExpiresAt = (authResult.expiresIn * 1000) + dateTimeProvider.now();
        this.sessionCache.accessToken = authResult.accessToken;
        this.sessionCache.idToken = authResult.idToken;
        this.sessionCache.expiresAt = tokenExpiresAt;
        this.scheduleRenewal();
    }
    getSession() {
        return this.sessionCache;
    }
    storeApplicationState() {
        if (!window.location.pathname.includes(this.config.callbackPath)) {
            const applicationState = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            BrowserWindow.setCacheValue(this.APPLICATION_STATE_KEY, applicationState, BrowserStorageTypes.Session);
        }
    }
    getQueryParamValue(paramName) {
        return new URLSearchParams(decodeURIComponent(window.location.search)).get(paramName);
    }
    storeLoginState(emailAddress, runtimeOverrides) {
        const loginState = {
            emailAddress: '',
            loginRuntimeOverrides: {}
        };
        if (emailAddress) {
            loginState.emailAddress = emailAddress;
        }
        if (runtimeOverrides !== loginState.loginRuntimeOverrides) {
            loginState.loginRuntimeOverrides = runtimeOverrides;
        }
        const encodedLoginState = btoa(JSON.stringify(loginState));
        BrowserWindow.setCacheValue(this.LOGIN_STATE_KEY, encodedLoginState, BrowserStorageTypes.Session);
    }
    retrieveLoginState() {
        const emptyLoginRuntimeState = {
            emailAddress: '',
            loginRuntimeOverrides: {}
        };
        const encodedLoginState = BrowserWindow.getCacheValue(this.LOGIN_STATE_KEY, BrowserStorageTypes.Session);
        return (encodedLoginState) ? JSON.parse(atob(encodedLoginState)) : emptyLoginRuntimeState;
    }
}
AuthService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthService, deps: [{ token: i1$1.Router }, { token: AUTH_CONFIG }, { token: AuthApiService }], target: i0.ɵɵFactoryTarget.Injectable });
AuthService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Router }, { type: AuthConfiguration, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG]
                }] }, { type: AuthApiService }]; } });

class AuthCallbackComponent {
    constructor(authService) {
        this.authService = authService;
    }
    ngOnInit() {
        this.authService.handleAuthentication();
        this.authService.scheduleRenewal(window.location.pathname);
    }
}
AuthCallbackComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthCallbackComponent, deps: [{ token: AuthService }], target: i0.ɵɵFactoryTarget.Component });
AuthCallbackComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AuthCallbackComponent, selector: "dat-auth-callback", ngImport: i0, template: ``, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthCallbackComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'dat-auth-callback',
                    template: ``
                }]
        }], ctorParameters: function () { return [{ type: AuthService }]; } });

class AuthInterceptor {
    constructor(config, authService) {
        this.config = config;
        this.authService = authService;
    }
    intercept(req, next) {
        if (this.targetedDomainUrl(req.url)) {
            return next.handle(req)
                .pipe(tap(res => this.onNext(res), error => this.onError(error)));
        }
        // URL's domain is not in the list of those requiring auth so do nothing
        return next.handle(req);
    }
    onNext(res) { }
    onError(error) {
        if (error instanceof HttpErrorResponse && this.config.loginIfApiCallNotAuthorized) {
            const errMsg = error.message;
            console.debug(`Handling an http response error ${error.status}: ${errMsg}`);
            if (error.status === 401) {
                const storedLoginState = this.authService.retrieveLoginState();
                this.authService.login('', storedLoginState.emailAddress, storedLoginState.loginRuntimeOverrides);
            }
        }
    }
    targetedDomainUrl(reqUrl) {
        const inTargetedDomains = ((member) => {
            return reqUrl.toLowerCase().includes(member);
        });
        return [...AuthConfiguration.apiTargetedDomains, ...this.config.extendedApiTargetedDomains].some(inTargetedDomains);
    }
}
AuthInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthInterceptor, deps: [{ token: AUTH_CONFIG }, { token: AuthService }], target: i0.ɵɵFactoryTarget.Injectable });
AuthInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: AuthConfiguration, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG]
                }] }, { type: AuthService }]; } });

class JwtModuleConfigService {
    constructor(config, authService) {
        this.config = config;
        this.authService = authService;
        this.allowedDomains = AuthConfiguration.apiTargetedDomains;
        this.tokenGetter = async () => {
            let token = '';
            try {
                token = await this.authService.getTokenSilently();
            }
            catch (err) {
            }
            return token;
        };
        this.allowedDomains = [...this.allowedDomains, ...this.config.extendedApiTargetedDomains];
    }
}
JwtModuleConfigService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: JwtModuleConfigService, deps: [{ token: AUTH_CONFIG }, { token: AuthService }], target: i0.ɵɵFactoryTarget.Injectable });
JwtModuleConfigService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: JwtModuleConfigService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: JwtModuleConfigService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: AuthConfiguration, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG]
                }] }, { type: AuthService }]; } });

class AuthModule {
}
AuthModule.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthModule, providedIn: 'root' });
AuthModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AuthModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: AuthModule, declarations: [AuthCallbackComponent], imports: [i1$2.JwtModule], exports: [AuthCallbackComponent] });
AuthModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthModule, providers: [
        AuthService,
        JwtModuleConfigService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        }
    ], imports: [JwtModule.forRoot({
            jwtOptionsProvider: {
                provide: JWT_OPTIONS,
                useClass: JwtModuleConfigService
            }
        })] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthModule, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }, {
            type: NgModule,
            args: [{
                    imports: [
                        JwtModule.forRoot({
                            jwtOptionsProvider: {
                                provide: JWT_OPTIONS,
                                useClass: JwtModuleConfigService
                            }
                        })
                    ],
                    declarations: [
                        AuthCallbackComponent
                    ],
                    providers: [
                        AuthService,
                        JwtModuleConfigService,
                        {
                            provide: HTTP_INTERCEPTORS,
                            useClass: AuthInterceptor,
                            multi: true
                        }
                    ],
                    exports: [
                        AuthCallbackComponent
                    ]
                }]
        }] });

class OAuthProfileError extends Error {
    constructor(msg) {
        super(msg);
        Object.setPrototypeOf(this, new.target.prototype);
    }
}
class OAuthProfile {
    constructor(oAuthProfile) {
        if (oAuthProfile) {
            if (!(oAuthProfile instanceof Object)) {
                throw new OAuthProfileError('Provided profile must be an object type');
            }
            if (!oAuthProfile.sub) {
                throw new OAuthProfileError('JSON profile must contain a sub property');
            }
            this.oAuthId = oAuthProfile.sub;
            // Handle oAuthProfiles with missing name property
            this.name = oAuthProfile.name || oAuthProfile.nickname || oAuthProfile.email || '';
            this.nickName = oAuthProfile?.nickname;
            this.csbId = oAuthProfile['https://dat.com/csb_id'];
            this.csbOrgId = oAuthProfile['https://dat.com/user_org'];
            this.officeId = oAuthProfile['https://dat.com/office_id'];
            this.userId = oAuthProfile['https://dat.com/user_id'];
            this.phoneNumber = oAuthProfile['https://dat.com/phone_number'];
            this.groupId = oAuthProfile['https://dat.com/group_id'];
            if (oAuthProfile.email) {
                this.emailAddress = oAuthProfile.email;
                this.emailVerified = oAuthProfile.email_verified;
            }
            else {
                this.userName = oAuthProfile.nickname;
            }
            this.picture = oAuthProfile.picture;
            this.updatedAt = oAuthProfile.updated_at;
        }
        else {
            this.oAuthId = null;
            this.nickName = '';
            this.name = '';
        }
    }
}

class AuthGuardService {
    constructor(authService) {
        this.authService = authService;
    }
    async canActivate() {
        return await this.canActivateOrLoad();
    }
    async canLoad() {
        return await this.canActivateOrLoad();
    }
    async canActivateOrLoad() {
        if (this.authService.isAuthenticated()) {
            this.authService.scheduleRenewal();
            return Promise.resolve(true);
        }
        else {
            try {
                await this.authService.getTokenSilently();
                console.debug('Auth guard service canActivate, got new access token');
                return Promise.resolve(true);
            }
            catch (e) {
                const storedLoginState = this.authService.retrieveLoginState();
                this.authService.login('', storedLoginState.emailAddress, storedLoginState.loginRuntimeOverrides);
            }
        }
    }
}
AuthGuardService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthGuardService, deps: [{ token: AuthService }], target: i0.ɵɵFactoryTarget.Injectable });
AuthGuardService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthGuardService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthGuardService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: AuthService }]; } });

class UserProfileService {
    constructor(authService) {
        this.authService = authService;
        this.userProfileSource = new BehaviorSubject(new OAuthProfile());
        this.userProfile$ = this.userProfileSource.asObservable();
        this.win = new BrowserWindow();
        this.win.browserWindow = window;
        this.isLoggedIn = false;
        this.initialize();
        this.subscribeToFetchedOAuthProfile();
        this.subscribeToLogin();
    }
    subscribeToFetchedOAuthProfile() {
        this.authService.oAuthProfileResult$.subscribe((fetchedOAuthProfile) => this.setOAuthProfile(fetchedOAuthProfile));
    }
    subscribeToLogin() {
        this.authService.loggedIn$.subscribe((loggedIn) => this.handleLoggedIn(loggedIn));
    }
    handleLoggedIn(loggedIn) {
        if (this.isLoggedIn !== loggedIn) {
            this.isLoggedIn = loggedIn;
            this.publishNewProfile(this.oAuthProfile);
        }
    }
    setOAuthProfile(fetchedOAuthProfile) {
        if (!fetchedOAuthProfile) {
            this.initialize();
            return;
        }
        try {
            this.oAuthProfile = new OAuthProfile(fetchedOAuthProfile);
            this.publishNewProfile(this.oAuthProfile);
        }
        catch (err) {
            console.log('error creating oauth profile model! ', err);
        }
    }
    initialize() {
        this.oAuthProfile = new OAuthProfile();
        this.publishNewProfile(this.oAuthProfile);
    }
    publishNewProfile(profile) {
        if (this.isLoggedIn) {
            this.userProfileSource.next(profile);
        }
        else {
            this.userProfileSource.next(new OAuthProfile());
        }
    }
}
UserProfileService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UserProfileService, deps: [{ token: AuthService }], target: i0.ɵɵFactoryTarget.Injectable });
UserProfileService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UserProfileService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UserProfileService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: AuthService }]; } });

var ScreenHint;
(function (ScreenHint) {
    ScreenHint["SIGNUP"] = "signup";
})(ScreenHint || (ScreenHint = {}));

/*
 * Public API Surface of auth
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AUTH_CONFIG, AuthApiService, AuthCallbackComponent, AuthConfiguration, AuthGuardService, AuthModule, AuthService, OAuthProfile, OAuthProfileError, PageMode, ScreenHint, UserAuthorizations, UserPermissions, UserProfileService, UserRoles, ViewTypes };
